/**
 * @fileoverview Firestore-based question reporting system.
 * 
 * This service handles the storage and management of user reports
 * for question quality control across all game types and languages.
 */

import { db } from '../firebase/config';
import { 
  addDoc, 
  collection, 
  Timestamp, 
  query, 
  where, 
  orderBy, 
  getDocs,
  updateDoc,
  doc,
  deleteDoc,
  DocumentData,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

// Enhanced status options for better tracking
export type ReportStatus = 'Reported' | 'In Review' | 'In Progress' | 'No Action' | 'On Hold' | 'Completed';

// Report data structure
export interface ReportData {
  id?: string; // Firestore document ID
  timestamp: string;
  gameType: string;
  language: string;
  questionId: string;
  questionText: string;
  reportReason: string;
  improvementSuggestion?: string;
  reporterEmail: string;
  reporterId?: string;
  status: ReportStatus;
  adminNotes?: string;
  reviewerId?: string;
  reviewerEmail?: string;
  reviewedAt?: string;
  fixedInVersion?: string;
  collectionId?: string;
  reportId: string; // Unique identifier for the report
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

class QuestionReportService {
  private readonly COLLECTION_NAME = 'questionReports';

  /**
   * Generate a unique report ID
   */
  private generateReportId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    return `RPT-${timestamp}-${random}`;
  }

  /**
   * Submit a question report to Firestore
   */
  async submitReport(reportData: Omit<ReportData, 'timestamp' | 'status' | 'reportId' | 'createdAt' | 'updatedAt'>): Promise<{ success: boolean; reportId?: string; error?: string }> {
    try {
      // Generate unique report ID
      const reportId = this.generateReportId();
      
      // Add timestamp and initial status
      const fullReportData: Omit<ReportData, 'id'> = {
        ...reportData,
        timestamp: new Date().toISOString(),
        status: 'Reported', // Initial status
        reportId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), fullReportData);
      
      console.log('✅ Report submitted successfully with ID:', reportId, 'Document ID:', docRef.id);
      return { success: true, reportId };
    } catch (error) {
      console.error('❌ Error submitting report:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get all reports with optional filtering
   */
  async getReports(filters?: {
    status?: ReportStatus;
    gameType?: string;
    language?: string;
    limit?: number;
  }): Promise<{ reports: ReportData[]; error?: string }> {
    try {
      let q = query(collection(db, this.COLLECTION_NAME), orderBy('createdAt', 'desc'));
      
      // Apply filters
      if (filters?.status) {
        q = query(q, where('status', '==', filters.status));
      }
      if (filters?.gameType) {
        q = query(q, where('gameType', '==', filters.gameType));
      }
      if (filters?.language) {
        q = query(q, where('language', '==', filters.language));
      }

      const querySnapshot = await getDocs(q);
      const reports: ReportData[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data() as ReportData;
        reports.push({
          ...data,
          id: doc.id
        });
      });

      // Apply limit if specified
      const limitedReports = filters?.limit ? reports.slice(0, filters.limit) : reports;
      
      return { reports: limitedReports };
    } catch (error) {
      console.error('❌ Error fetching reports:', error);
      return { reports: [], error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get a single report by ID
   */
  async getReportById(reportId: string): Promise<{ report?: ReportData; error?: string }> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME), 
        where('reportId', '==', reportId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { error: 'Report not found' };
      }

      const doc = querySnapshot.docs[0];
      const data = doc.data() as ReportData;
      
      return { report: { ...data, id: doc.id } };
    } catch (error) {
      console.error('❌ Error fetching report:', error);
      return { error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Update report status and admin notes
   */
  async updateReportStatus(
    reportId: string, 
    newStatus: ReportStatus, 
    adminNotes?: string,
    reviewerId?: string,
    reviewerEmail?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // First find the document by reportId
      const q = query(
        collection(db, this.COLLECTION_NAME), 
        where('reportId', '==', reportId)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { success: false, error: 'Report not found' };
      }

      const docRef = querySnapshot.docs[0].ref;
      const updateData: Partial<ReportData> = {
        status: newStatus,
        updatedAt: Timestamp.now()
      };

      if (adminNotes !== undefined) {
        updateData.adminNotes = adminNotes;
      }

      if (reviewerId) {
        updateData.reviewerId = reviewerId;
      }

      if (reviewerEmail) {
        updateData.reviewerEmail = reviewerEmail;
      }

      if (newStatus !== 'Reported') {
        updateData.reviewedAt = new Date().toISOString();
      }

      await updateDoc(docRef, updateData);
      
      console.log(`✅ Report status updated to "${newStatus}" for report ID: ${reportId}`);
      return { success: true };
    } catch (error) {
      console.error('❌ Error updating report status:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Delete a question report
   */
  async deleteReport(reportId: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Find the document by reportId
      const q = query(collection(db, this.COLLECTION_NAME), where('reportId', '==', reportId));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return { success: false, error: 'Question report not found' };
      }
      
      // Delete the document
      const docRef = querySnapshot.docs[0].ref;
      await deleteDoc(docRef);
      
      console.log('✅ Question report deleted successfully:', reportId);
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting question report:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get report statistics
   */
  async getReportStats(): Promise<{
    total: number;
    reported: number;
    inReview: number;
    inProgress: number;
    noAction: number;
    onHold: number;
    completed: number;
    byGameType: Record<string, number>;
    error?: string;
  }> {
    try {
      const { reports } = await this.getReports();
      
      const stats = {
        total: reports.length,
        reported: 0,
        inReview: 0,
        inProgress: 0,
        noAction: 0,
        onHold: 0,
        completed: 0,
        byGameType: {} as Record<string, number>
      };

      reports.forEach(report => {
        // Count by status
        switch (report.status) {
          case 'Reported':
            stats.reported++;
            break;
          case 'In Review':
            stats.inReview++;
            break;
          case 'In Progress':
            stats.inProgress++;
            break;
          case 'No Action':
            stats.noAction++;
            break;
          case 'On Hold':
            stats.onHold++;
            break;
          case 'Completed':
            stats.completed++;
            break;
        }

        // Count by game type
        const gameType = report.gameType;
        stats.byGameType[gameType] = (stats.byGameType[gameType] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('❌ Error getting report stats:', error);
      return {
        total: 0,
        reported: 0,
        inReview: 0,
        inProgress: 0,
        noAction: 0,
        onHold: 0,
        completed: 0,
        byGameType: {},
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get reports by user (for user dashboard)
   */
  async getUserReports(userId: string): Promise<{ reports: ReportData[]; error?: string }> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('reporterId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const reports: ReportData[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data() as ReportData;
        reports.push({
          ...data,
          id: doc.id
        });
      });

      return { reports };
    } catch (error) {
      console.error('❌ Error fetching user reports:', error);
      return { reports: [], error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Get available status options
   */
  getStatusOptions(): ReportStatus[] {
    return ['Reported', 'In Review', 'In Progress', 'No Action', 'On Hold', 'Completed'];
  }

  /**
   * Get status color for UI
   */
  getStatusColor(status: ReportStatus): string {
    switch (status) {
      case 'Reported':
        return '#ef4444'; // red
      case 'In Review':
        return '#f59e0b'; // amber
      case 'In Progress':
        return '#3b82f6'; // blue
      case 'No Action':
        return '#6b7280'; // gray
      case 'On Hold':
        return '#8b5cf6'; // purple
      case 'Completed':
        return '#10b981'; // green
      default:
        return '#6b7280'; // gray
    }
  }
}

// Export singleton instance
export const questionReportService = new QuestionReportService();

// Hook for easy integration
export const useQuestionReports = () => {
  const { user } = useAuth();
  
  const submitQuestionReport = async (
    gameType: string,
    questionId: string,
    questionText: string,
    reportReason: string,
    improvementSuggestion?: string,
    language?: string
  ): Promise<{ success: boolean; reportId?: string; error?: string }> => {
    if (!user?.email) {
      return { success: false, error: 'User must be logged in to submit reports' };
    }

    const reportData: Omit<ReportData, 'timestamp' | 'status' | 'reportId' | 'createdAt' | 'updatedAt'> = {
      gameType,
      language: language || 'en',
      questionId: String(questionId),
      questionText,
      reportReason,
      improvementSuggestion,
      reporterEmail: user.email,
      reporterId: user.uid,
      collectionId: `${gameType}V1_${language || 'en'}`
    };

    return await questionReportService.submitReport(reportData);
  };

  const updateReportStatus = async (
    reportId: string, 
    newStatus: ReportStatus, 
    adminNotes?: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!user?.email) {
      return { success: false, error: 'User must be logged in to update reports' };
    }

    return await questionReportService.updateReportStatus(
      reportId, 
      newStatus, 
      adminNotes,
      user.uid,
      user.email
    );
  };

  const getUserReports = async (): Promise<{ reports: ReportData[]; error?: string }> => {
    if (!user?.uid) {
      return { reports: [], error: 'User must be logged in' };
    }

    return await questionReportService.getUserReports(user.uid);
  };

  return {
    submitQuestionReport,
    updateReportStatus,
    getUserReports,
    getReports: questionReportService.getReports.bind(questionReportService),
    getReportById: questionReportService.getReportById.bind(questionReportService),
    getReportStats: questionReportService.getReportStats.bind(questionReportService),
    getStatusOptions: questionReportService.getStatusOptions.bind(questionReportService),
    getStatusColor: questionReportService.getStatusColor.bind(questionReportService)
  };
};

// Export types for use in other components
export type { ReportData, ReportStatus };
